import Actions from './actions';
import auth_helper from 'webapp/helpers/auth_helper';
import LocationHelper from 'webapp/helpers/location_helper';
import Constants from '../helpers/constants';
import StepDocsHelper from '../helpers/financing_request/step_docs_helper';

export const customerLoader = {
  storeKeys: ['customer'],
  loadDataFn: ({ proposal }) => Actions.fetchCustomer(proposal?.customerId),
};

export const prescreenCustomerLoader = {
  checkDataFn: ({ customer }) => customer?.hasPrescreen,
  storeKeys: ['prescreenProducts'],
  loadDataFn: ({ proposal, userFunctionalityAcls }) => {
    const quickCheckEnabled = userFunctionalityAcls?.some(
      (element) => element?.featureCode === Constants.FUNCTIONALITY_ACL_FEATURE_CODE_QUICK_CHECK
    );
    return quickCheckEnabled && Actions.fetchPrescreen(proposal.customerId);
  },
  optional: true,
};

export const eligibilityOptionalLoader = {
  checkDataFn: ({ customer, eligibility }) => eligibility || customer?.currentCreditCheckId === null,
  loadDataFn: ({ customer, homeownerData, view: { consentToken } }) => {
    return Actions.fetchEligibility({ customerId: customer?.id || homeownerData?.customer?.id, consentToken });
  },
  optional: true,
};

export const plaidLinkOptionalLoader = {
  // checkDataFn: ({ customer, eligibility }) => eligibility || customer?.currentCreditCheckId === null,
  storeKeys: ['plaidLink'],
  loadDataFn: ({ customer, homeownerData, view: { consentToken } }) => {
    return Actions.fetchPlaidLink({ customerId: customer?.id || homeownerData?.customer?.id, consentToken });
  },
  optional: true,
};

export const stipStatusNotesLoader = {
  checkDataFn: () => false,
  storeKeys: ['stipStatusNotesArr'],
  loadDataFn: ({ customer, view: { token } }) => Actions.fetchStipStatusNotes({ customerId: customer?.id, token }),
  optional: true,
};

export const energyDetailsLoader = {
  storeKeys: ['energyDetails'],
  loadDataFn: ({ proposal }) => Actions.fetchEnergyDetails(proposal.customerId),
};

export const financingMethodLoader = {
  storeKeys: ['financingMethod'],
  checkDataFn: ({ proposal, view }) =>
    proposal && Number(LocationHelper.searchAsObj().proposalId || view.proposalId) === proposal.id,
  loadDataFn: ({ proposal, view }) => {
    let proposalId = view?.proposalId || LocationHelper.searchAsObj().proposalId;
    if (proposalId) {
      Actions.getFinancingMethod({ proposalId });
    } else if (proposal) {
      Actions.getFinancingMethod({ proposalId: proposal.id });
    }
  },
};

export const homeownerDataLoader = {
  storeKeys: ['homeownerData'],
  loadDataFn: ({ view }) => {
    return Actions.fetchHomeownerStips(view?.token || LocationHelper.searchAsObj().token);
  },
  optional: true,
};

export const prescreenProductsLoader = {
  storeKeys: ['prescreenProducts'],
  loadDataFn: async ({ proposal: { customerId }, userFunctionalityAcls }) => {
    const quickCheckEnabled = userFunctionalityAcls?.some(
      (element) => element?.featureCode === Constants.FUNCTIONALITY_ACL_FEATURE_CODE_QUICK_CHECK
    );
    if (quickCheckEnabled) {
      let preScreenProducts = await Actions.fetchPrescreen(customerId);
      if (!preScreenProducts) {
        Actions.beginPollPrescreen(customerId);
      }
      return preScreenProducts;
    }
  },
};

export const proposalLoader = {
  checkDataFn: ({ proposal, view }) => {
    return proposal && Number(view?.proposalId || LocationHelper.searchAsObj().proposalId) === proposal.id;
  },
  loadDataFn: ({ proposal, view }) =>
    view?.proposalId || LocationHelper.searchAsObj().proposalId
      ? Actions.fetchProposal(view?.proposalId || LocationHelper.searchAsObj().proposalId)
      : Actions.fetchProposal(proposal.id),
};

export const userLoader = {
  storeKeys: ['user'],
  loadDataFn: () => Actions.fetchAuthenticatedUser(),
};

export const userLoaderOptional = {
  storeKeys: ['user'],
  optional: () => auth_helper.getImpersonateCookie() || !auth_helper.getSessionToken() || auth_helper.isShortAuth(),
  loadDataFn: async () => {
    if (auth_helper.getImpersonateCookie() || (auth_helper.getSessionToken() && !auth_helper.isShortAuth())) {
      await Actions.fetchAuthenticatedUser();
    }
  },
};

export const financingRequestLoader = {
  checkDataFn: () => false,
  storeKeys: ['financingRequest'],
  loadDataFn: ({ view, proposal }) => {
    if (proposal?.financingRequestId && (view?.customerId || proposal?.customerId))
      Actions.fetchFinancingRequest({ customerId: view?.customerId || proposal?.customerId });
  },
  optional: true,
};

/**
 * Used to blindly grab a financing request regardless of the proposal having a frid on it
 */
export const financingRequestBlindLoader = {
  checkDataFn: () => false,
  storeKeys: ['financingRequest'],
  loadDataFn: ({ view }) => {
    if (view?.customerId) Actions.fetchFinancingRequest({ customerId: view.customerId });
  },
  optional: true,
};

export const financingRequestLoaderWithGeneratedDocs = {
  checkDataFn: () => false,
  storeKeys: ['financingRequest'],
  loadDataFn: ({ view, proposal }) => {
    if (proposal !== null && proposal.financingRequestId !== null)
      Actions.fetchFinancingRequest({ waitOnGeneratedDocs: true, customerId: view?.customerId });
  },
  optional: true,
};

export const workflowProofOverrideLoader = {
  storeKeys: ['workflowProofOverrides'],
  checkDataFn: ({ user, financingRequest }) =>
    user?.atype === 'CPF' && StepDocsHelper.financingRequestHasDocBySku(financingRequest, 'SystemMonitoringType'),
  loadDataFn: async ({ customer }) => await Actions.fetchWorkflowProofOverrides({ customerId: customer.id }),
};

export const customerInverterMonitoringLoader = {
  storeKeys: ['customerInverterMonitoring'],
  loadDataFn: async ({ customer }) => await Actions.fetchCustomerInverterMonitoring({ customerId: customer.id }),
};

export const homeownerEligibilityDataLoader = {
  storeKeys: ['customer', 'user'],
  loadDataFn: ({ view: { consentToken } }) => Actions.fetchHomeownerEligibilityData(consentToken),
};

export const verifiedSignersLoader = {
  storeKeys: ['verifiedSigners'],
  loadDataFn: () => Actions.fetchVerifiedSigners(),
};

export const adminFinancingRequestLoader = {
  storeKeys: ['adminFinancingRequest'],
  loadDataFn: ({ view: { customerId } }) => {
    Actions.fetchAdminFinancingRequest({ customerId });
  },
};

export const adminDecisioningPageDataLoader = {
  checkDataFn: ({ decisioningPageData, view: { decisionId } }) =>
    decisioningPageData && Number(decisionId) === Number(decisioningPageData.id),
  storeKeys: ['decisioningPageData', 'decisioningPageUsers', 'decisioningPageSwitchInfo'],
  loadDataFn: async ({ decisionPageData, view: { decisionId }, decisioningPageData }) => {
    const { customerId } = await Actions.fetchDecisionPage({ id: decisionId ? decisionId : decisionPageData.id });
    Actions.fetchDecisionUsers();
    Actions.fetchSwitchDataForUWDash({ customerId });
  },
};

export const adminDecisioningTableDataLoader = {
  storeKeys: ['decisioningTableData'],
  loadDataFn: () => {
    Actions.fetchDecisionTable();
  },
};

export const adminDecisioningTableInactiveDataLoader = {
  storeKeys: ['decisioningTableInactiveData'],
  loadDataFn: () => {
    Actions.fetchDecisionTableInactive({ search: LocationHelper.searchAsObj().search });
  },
};

export const pricingProgramNamesLoader = {
  storeKeys: ['pricingProgramNames'],
  loadDataFn: () => Actions.fetchPricingProgramNames(),
};

export const teamUserLoader = {
  storeKeys: ['user', 'branchList', 'pricingGroupsList', 'ratesList', 'foundPricingGroup'],
  loadDataFn: async ({ view }) => {
    if (view.id) {
      await Actions.fetchUser(view.id);
    } else {
      Actions.newUser(view.coid);
    }
  },
  emptyIsStillData: true,
};

export const companyListLoader = {
  storeKeys: ['companyList'],
  loadDataFn: () => {
    Actions.fetchCompanyList();
  },
};

export const companyLoader = {
  storeKeys: ['company'],
  loadDataFn: ({ view }) => Actions.fetchCompany(view.coid),
};

export const companyDetailsLoader = {
  storeKeys: ['company'],
  loadDataFn: ({ view }) => Actions.fetchCompanyWithAdmin(view.coid),
  checkDataFn: ({ company }) => company?.company && company?.admin,
};

export const companyElementTypesLoader = {
  storeKeys: ['companyElementTypes'],
  loadDataFn: ({ view }) => Actions.fetchCompanyElementTypeForm(view.coid),
  emptyIsStillData: true,
};

export const paymentProgramLoader = {
  storeKeys: ['companyPaymentPrograms'],
  loadDataFn: ({ view }) => Actions.fetchCompanyPaymentPrograms(view.coid),
};

export const companyAuditLogLoader = {
  storeKeys: ['auditLogs'],
  loadDataFn: ({ view }) => Actions.fetchCompanyAuditLogs(view.coid),
};

export const pricingGroupLoader = {
  storeKeys: ['foundPricingGroup'],
  loadDataFn: ({ view }) => Actions.getPricingGroup({ coid: view.coid, pricingId: view.pricingId }),
};

export const branchLoader = {
  storeKeys: ['foundBranch'],
  loadDataFn: ({ view }) => Actions.getBranch({ coid: view.coid, branchId: view.branchId }),
};

export const branchListLoader = {
  storeKeys: ['branchList'],
  loadDataFn: ({ view }) => Actions.getBranchList(view.coid),
  emptyIsStillData: true,
};

export const licenseLoader = {
  storeKeys: ['foundLicense'],
  loadDataFn: ({ view }) => Actions.getLicense({ coid: view.coid, licenseId: view.licenseId }),
};

export const licenseListLoader = {
  storeKeys: ['licenseList'],
  loadDataFn: ({ view }) => Actions.getLicenseList(view.coid),
  emptyIsStillData: true,
};
export const licensesForProposal = {
  storeKeys: ['licenseList'],
  loadDataFn: ({ user }) => Actions.getLicenseList(user?.company?.coid),
  emptyIsStillData: true,
};

export const userLicenseListLoader = {
  storeKeys: ['userLicenseList'],
  loadDataFn: ({ user }) => Actions.getUserLicenseList({ coid: user?.company?.coid, branchId: user?.branch?.id }),
  emptyIsStillData: true,
};

export const authorizedSignerLoader = {
  storeKeys: ['foundAuthorizedSigner'],
  loadDataFn: ({ view }) => Actions.fetchAuthorizedSigner({ id: view.signerId, coid: view.coid }),
};

export const authorizedSignerListLoader = {
  storeKeys: ['authorizedSignerList'],
  loadDataFn: ({ view }) => Actions.fetchAuthorizedSignerList({ coid: view.coid }),
  emptyIsStillData: true,
};

export const subscriptionListLoader = {
  storeKeys: ['subscriptionList'],
  loadDataFn: ({ view }) => Actions.getSubscriptionList(view.coid),
  emptyIsStillData: true,
};

export const subscriptionLoader = {
  storeKeys: ['foundSubscription'],
  loadDataFn: ({ view }) => Actions.getSubscription({ coid: view.coid, subscriptionId: view.subscriptionId }),
  emptyIsStillData: true,
};

export const customerBannerDataLoader = {
  storeKeys: ['customerBannerData'],
  loadDataFn: ({ customer }) => {
    Actions.fetchCustomerBannerData({ customerId: customer?.id, status: customer?.status });
  },
  emptyIsStillData: true,
};

export const customerHoldTypesLoader = {
  checkDataFn: ({ customer }) => customer?.currentCreditCheckId && customer?.holdCategorySku,
  // ignoreIfTrueFn: ({ customer }) => !customer?.holdCategorySku,
  storeKeys: ['customerHoldTypes'],
  loadDataFn: ({ customer }) => {
    Actions.fetchCustomerHoldTypes();
  },
  emptyIsStillData: true,
};

export const wcQuestionLoader = {
  storeKeys: ['welcomeCallResponses'],
  loadDataFn: ({ view }) => {
    Actions.fetchWCQuestions(view.data);
  },
};

export const customerHistoryLoader = {
  storeKeys: [
    'customerHistory',
    'fridHistory',
    'creditCheckHistory',
    'snapshotHistory',
    'disbursementHistory',
    'notificationHistory',
    'customerAuditTrail',
    'sponsorApiEventHistory',
    'customerWebhookHistory',
  ],
  loadDataFn: ({ view }) => Actions.fetchCustomerHistoryDetails(view?.data),
  emptyIsStillData: true,
};

export const decisionNotesLoader = {
  storeKeys: ['decisionNotesList'],
  loadDataFn: ({ view }) => {
    Actions.fetchDecisionNotes({ caseId: view.decisionId });
  },
  emptyIsStillData: true,
};

export const stepDisbursementsLoader = {
  storeKeys: ['customerDisbursementInfo'],
  loadDataFn: async ({ customer: { id: customerId }, userFunctionalityAcls }) =>
    await Actions.fetchDisbursementsForStepMenu({ customerId, userFunctionalityAcls }),
  emptyIsStillData: true,
};
